import { channelwill } from '@channelwill/sdk'
import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import axios, { isAxiosError } from 'axios'
import type { DeferredPromise } from 'p-defer'
import pDefer from 'p-defer'
import type { ResponseWrapper } from '@/api/types'
import { GLOBAL_API_ERROR, GLOBAL_API_REQUEST_ID } from '@/constants/keys'
import auth from '@/lib/auth/oAuth'
import { errorBridge } from '@/lib/error-bridge-state'
import { $env } from '@/lib/location-env'

const URL = `${process.env.PUBLIC_BASE_URL}/api/v1/`

const client = axios.create({
  baseURL: URL,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
  },
})

// 对请求前的拦截器
client.interceptors.request.use(
  async (config) => {
    const token = await auth.getToken()
    config.headers['Authorization'] = token

    try {
      if (
        window.shopify &&
        window.shopify.environment.embedded &&
        !$env.localhost
      ) {
        config.headers['X-Shopify-Session-Token'] = await shopify.idToken()
      }

      if ($env.localhost) {
        config.headers['X-loloyal-token'] = process.env.PUBLIC_TEST_SHOP_TOKEN
      }
    } catch (e) {
      console.log('sessiontoekn notfound: ', e)
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// 对响应的拦截器
client.interceptors.response.use(
  (response: AxiosResponse<ResponseWrapper>) => {
    if (response.data?.code && response.data.code !== 200) {
      const allowRetry = !channelwill.isEmbedded()
      if (allowRetry && response.data.code > 500 && auth.state.authenticated) {
        // 已经在登录状态的情况下，token失效
        return retry(response.config)
      }
      return Promise.reject(response.data)
    }
    const condition1 = response.data.data && response.data.success
    const condition2 = response.data.data === null && response.data.success
    if (condition1 || condition2) {
      response.data = response.data.data
    } else if (!response.data.data) return response
    else Promise.reject(response.data)

    return response
  },
  (error: any) => {
    if (isAxiosError(error) && error.status === 401) {
      // 401 错误不经过 errorBridge，直接抛出
    } else {
      errorBridge.show(
        GLOBAL_API_ERROR,
        `${error.message} in ${error.config.url}`,
        error?.response?.headers?.[GLOBAL_API_REQUEST_ID],
      )
    }
    return Promise.reject(error)
  },
)

export default client

let authDeferredPromise: DeferredPromise<boolean> | null = null
async function retry(config: AxiosRequestConfig) {
  if (!authDeferredPromise) {
    authDeferredPromise = pDefer()
    auth.reAuth().then(() => {
      authDeferredPromise?.resolve(true)
      authDeferredPromise = null
    })
  }
  await authDeferredPromise.promise

  if (config.headers) {
    config.headers['Authorization'] = auth.getToken()
  }
  return client(config)
}
